import { isEmpty as _isEmpty, pickBy as _pickBy } from "lodash";
import { Validator, Rules } from "vee-validate";

const CONTEXT_FORMS_FORM_VALUES = "forms/formValues";
const CONTEXT_FORMS_TITLE_DEFAULT_SELECTED = "forms/titleDefaultSelected";
const CONTEXT_FORMS_VALIDATION_RESULTS = "forms/validationResults";
const FIELD_NON_INPUT_TYPES = ["radio", "select", "textarea", "checkbox"];
const FORM_VALUES_COUNTRY_US = "US";

export default class FormHelper {
  constructor(store, formFields) {
    this.store = store;
    this.formFields = formFields;
    this.selectedAddress = {
      address1: null,
      zip5: null,
    }
  }

  addressAutocompleteSelected() {
    const { addressAutocomplete } = this.store.state.forms;
    const place = addressAutocomplete.getPlace();
    const unfilteredAddressComponents = place.address_components;
    const addressComponents = this.extractGoogleAutocompleteAddress(
      unfilteredAddressComponents
    );

    const streetNumber = addressComponents.street_number || "";
    const route = addressComponents.route || "";
    const address1 = `${streetNumber} ${route}`.trim();
    const zip5 =
      addressComponents.postal_code ||
      addressComponents.postal_code_prefix ||
      "";
    const zip4 = addressComponents.postal_code_suffix || "";
    const state = addressComponents.administrative_area_level_1 || '';
    const city = addressComponents.locality || '';
    const country = addressComponents.country || "";
    let county = addressComponents.administrative_area_level_2;
    county = county ? county.replace(' County', '') : '';

    // When the country is on for a campaign, there is always a default
    // value set for it. If no setting for country, we default to US
    // Thus, validation for the country will always pass for all other
    // address fields, which is why country must be checked first so
    // send the correct value for the country.
    if (this.formFields.country && country) {
      this.setFormFieldValueInStore(this.formFields.country, country);
    }

    if (this.formFields.address1 && address1) {
      this.setFormFieldValueInStore(this.formFields.address1, address1);
    }

    if (this.formFields.zip5 && zip5) {
      this.setFormFieldValueInStore(this.formFields.zip5, zip5);
    }

    if (county) {
      this.setFormFieldValueInStore(this.formFields.county, county);
    }

    if (city) {
      this.setFormFieldValueInStore(this.formFields.city, city);
    }

    if (state) {
      this.setFormFieldValueInStore(this.formFields.state, state);
    }

    if (zip4) {
      this.setFormFieldValueInStore(this.formFields.zip4, zip4);
    }

    if (place.geometry) {
      this.setFormFieldValueInStore(this.formFields.lat, place.geometry.location.lat());
      this.setFormFieldValueInStore(this.formFields.lon, place.geometry.location.lng());
      this.selectedAddress.address1 = address1;
      this.selectedAddress.zip5 = zip5;
    }
  }

  getSelectedAddress() {
    return this.selectedAddress;
  }

  // eslint-disable-next-line class-methods-use-this
  addressHasChanged(field, validationResults) {
    const { formValues } = this.store.state.forms;
    const requiredAddressFields = ["address1", "zip5"];
    const { address1, zip5, country } = formValues;
    const lastPayload = this.store.state.wdyk.prefetchStakeholdersPayload;
    let hasAddressChanged = false;

    if (
      lastPayload.address1 !== address1 ||
      lastPayload.zip5 !== zip5 ||
      lastPayload.country !== country
    ) {
      hasAddressChanged = true;
    }

    return (
      hasAddressChanged &&
      validationResults.isValid &&
      requiredAddressFields.includes(field.name) &&
      this.validateAllRequiredAddressFields()
    );
  }

  // add custom validation rule to limit zip codes to five digits
  // when country is US
  addZipValidationRule() {
    Validator.extend("digits5_if_us", value => {
      if (this.isCountryFieldValueUS()) {
        return Rules.digits(value, [5]);
      }

      return true;
    });
  }

  checkFieldValid(field) {
    const { formValues, validationResults } = this.store.state.forms;
    const requiredFields = Object.keys(this.formFields);
    const currentActionFieldSetting = this.formFields[field];
    const validationErrors = validationResults.default[field];

    const fieldEnabled = this.isFieldEnabled(currentActionFieldSetting);
    const fieldRequired = requiredFields.includes(field);
    const validationFailed =
      !_isEmpty(validationErrors) ||
      (validationErrors !== null && validationErrors !== undefined);
    const fieldNotPopulated = _isEmpty(formValues[field]);

    return !(
      fieldEnabled &&
      fieldRequired &&
      (validationFailed || fieldNotPopulated)
    );
  }

  // eslint-disable-next-line class-methods-use-this
  extractGoogleAutocompleteAddress(addressComponents) {
    const extractedComponents = [];

    addressComponents.forEach(addressComponent => {
      extractedComponents[addressComponent.types[0]] =
        addressComponent.short_name;
    });

    return extractedComponents;
  }

  // eslint-disable-next-line class-methods-use-this
  findAndParseStringByCountry(str) {
    const PARSE_COUNTRY_KEY_US = "US:";
    // const PARSE_COUNTRY_KEY_OTHER = "Other:";

    if (str === undefined) {
      return null;
    }

    const strOptions = str.split("|");
    const strMatcher = PARSE_COUNTRY_KEY_US;
    // const strMatcher = this.isCountryFieldValueUS
    //   ? PARSE_COUNTRY_KEY_US
    //   : PARSE_COUNTRY_KEY_OTHER;
    const strElement = strOptions.find(el => el.startsWith(strMatcher));

    if (!strElement) {
      return null;
    }

    return strElement.substring(strMatcher.length);
  }

  getCheckBoxFieldValue(field) {
    let fieldValue = this.getFieldValue(field);

    if (!field.options || !field.options.length) {
      return fieldValue;
    }

    if (typeof fieldValue === "string") {
      try {
        // turn multiple values into an array
        fieldValue = JSON.parse(fieldValue);
      } catch (e) {
        // turn single strings into an array
        fieldValue = [fieldValue];
      }
    }

    return fieldValue || [];
  }

  getCountryValue() {
    const countryDefault = "US";
    const { country } = this.store.state.forms.formValues;

    return !_isEmpty(country) ? country : countryDefault;
  }

  // eslint-disable-next-line class-methods-use-this
  getCustomFields(fields) {
    return _pickBy(fields, field => field.type === "custom");
  }

  getFieldDescription(field) {
    return this.getFieldProp(field, "description");
  }

  getFieldName(field) {
    return this.getFieldProp(field, "name");
  }

  // eslint-disable-next-line class-methods-use-this
  getFieldType(field) {
    const inputType = field.inputType.toLowerCase();

    if (FIELD_NON_INPUT_TYPES.indexOf(inputType) !== -1) {
      return inputType;
    }

    return "input";
  }

  getFieldPlaceholder(field, defaultValue = "") {
    return this.getFieldProp(field, "placeholder", defaultValue);
  }

  // eslint-disable-next-line class-methods-use-this
  getFieldProp(field, prop, defaultValue = "") {
    return field && field[prop] !== undefined ? field[prop] : defaultValue;
  }

  getFieldValue(field) {
    if (field === undefined) {
      return field;
    }

    return this.store.state.forms.formValues[field.name] || field.value;
  }

  // eslint-disable-next-line class-methods-use-this
  getFormFieldValidations(formFields) {
    const validations = {};
    const { formValues, validationResults } = this.store.state.forms;

    const defaultValidationResults = validationResults.default || {};

    Object.values(formFields).forEach(formField => {
      const isFieldRequired =
        (formField.validationRules && formField.validationRules.required) ||
        false;
      const isFieldEnabled = this.isFieldEnabled(formField);
      const hasFailedValidation = !_isEmpty(defaultValidationResults[formField.name]);
      const isFieldFilled =
        formValues[formField.name] && !_isEmpty(formValues[formField.name]);

      validations[formField.name] = {
        isFieldRequired,
        isFieldFilled,
        isFieldEnabled,
        hasFailedValidation
      };
    });

    return validations;
  }

  getSelectFieldValue(field) {
    const fieldValue = this.getFieldValue(field);
    return _isEmpty(fieldValue) ? "" : fieldValue;
  }

  // eslint-disable-next-line class-methods-use-this
  getValidationErrorMessage(validationResults) {
    return !validationResults.isValid ? validationResults.message : null;
  }

  getZipFieldPlaceholder(placeholderRaw, defaultValue = null) {
    if (_isEmpty(placeholderRaw)) {
      return defaultValue;
    }

    return this.findAndParseStringByCountry(placeholderRaw);
  }

  getZipFieldValidationError(validationErrorRaw) {
    // separates values list from the rest of the validation error
    const VALUES_LIST_DEMARCATION = "!!";

    if (_isEmpty(validationErrorRaw)) {
      return null;
    }

    const validationError = validationErrorRaw.split(
      VALUES_LIST_DEMARCATION
    )[0];
    const validationErrorValues = validationErrorRaw.split(
      VALUES_LIST_DEMARCATION
    )[1];
    const errorByCountry = this.findAndParseStringByCountry(
      validationErrorValues
    );

    return validationError.concat(errorByCountry);
  }

  initCountry(formFields) {
    if (formFields.country && formFields.country.name) {
      const selectedCountry = this.getCountryValue();
      this.setFormFieldValueInStore(formFields.country, selectedCountry);
    }
  }

  isCheckbox(field) {
    return this.getFieldType(field) === "checkbox";
  }

  isCheckBoxChecked(field, checkboxValue) {
    const currentCheckboxValue = this.getCheckBoxFieldValue(field);

    if (!field.options || !field.options.length) {
      return currentCheckboxValue === checkboxValue;
    }

    return currentCheckboxValue.indexOf(checkboxValue) !== -1;
  }

  isCountryFieldValueUS() {
    const country = this.getCountryValue();

    return country === FORM_VALUES_COUNTRY_US;
  }

  isInput(field) {
    return this.getFieldType(field) === "input";
  }

  isRadio(field) {
    return this.getFieldType(field) === "radio";
  }

  isSelect(field) {
    return this.getFieldType(field) === "select";
  }

  isSingleOptionCheckbox(field) {
    return (
      this.isCheckbox(field) &&
      !_isEmpty(field.options) &&
      field.options.length === 1
    );
  }

  isTextArea(field) {
    return this.getFieldType(field) === "textarea";
  }

  // eslint-disable-next-line class-methods-use-this
  isFirefox() {
    // https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
    return (
      typeof InstallTrigger !== "undefined" ||
      navigator.userAgent.indexOf("Firefox") > -1
    );
  }

  isFormValid(formFields) {
    let formIsValid = true;
    const formFieldValidations = this.getFormFieldValidations(formFields);

    Object.keys(formFieldValidations).forEach(formField => {
      const {
        isFieldRequired,
        isFieldEnabled,
        hasFailedValidation,
        isFieldFilled
      } = formFieldValidations[formField];

      if (
        isFieldEnabled &&
        (hasFailedValidation || (isFieldRequired && !isFieldFilled))
      ) {
        formIsValid = false;
      }
    });

    return formIsValid;
  }

  // https://paul.kinlan.me/detecting-when-autofill-happens/
  // eslint-disable-next-line class-methods-use-this
  registerOnAutoComplete(selector) {
    return new Promise(resolve => {
      const element = document.querySelector(selector);
      let hasKeyInteraction = false;

      element.addEventListener("input", e => {
        if (hasKeyInteraction === false) {
          resolve(e.target);
        }
      });

      element.addEventListener("keydown", () => {
        // If there is a keyboard interaction then we believe it is not autocomplete
        hasKeyInteraction = true;
      });
    });
  }

  isFieldEnabled(field) {
    return field && field.isOn === true;
  }

  isFieldRequired(field) {
    return field && field.validationRules && !_isEmpty(field.validationRules.required);
  }

  setFormFieldValueInStore(field, value) {
    if (value !== null && value !== undefined) {
      this.store.dispatch(CONTEXT_FORMS_FORM_VALUES, {
        fieldName: field.name,
        value
      });
    }
  }

  setValidationResultsInStore(field, validationErrors) {
    this.store.dispatch(CONTEXT_FORMS_VALIDATION_RESULTS, {
      action: "default", // this.currentAction
      fieldName: field.name,
      value: validationErrors
    });
  }

  toggleTitleDefaultSelectedState(field, value) {
    if (field.name === "title" && value === "") {
      this.store.dispatch(CONTEXT_FORMS_TITLE_DEFAULT_SELECTED, true);
    } else {
      this.store.dispatch(CONTEXT_FORMS_TITLE_DEFAULT_SELECTED, false);
    }
  }

  transformCustomFieldOptions(field) {
    const options = this.getFieldProp(field, "options", []);

    return options.map(option => ({ label: option, value: option }));
  }

  updateCheckboxValue(field, value, isChecked) {
    if (!field.options || !field.options.length) {
      return value;
    }

    const fieldValues = this.getCheckBoxFieldValue(field);
    const valueIndex = fieldValues.indexOf(value);

    if (!isChecked && valueIndex !== -1) {
      fieldValues.splice(valueIndex, 1);
    } else if (valueIndex === -1) {
      fieldValues.push(value);
    }

    return fieldValues;
  }

  validateAllRequiredAddressFields() {
    const addressFields = ["address1", "city", "state", "country", "zip5"];

    return addressFields.every(field => this.checkFieldValid(field));
  }
}
